/**
 * Configuration centralisée du système OpSecours
 * Tous les fichiers JSON de configuration sont importés et exportés depuis ce fichier
 */

import buildingsConfig from './buildings.json';
import personnelConfig from './personnel.json';
import vehiclesConfig from './vehicles.json';
import gameConfig from './game.json';
import uiConfig from './ui.json';
import apiConfig from './api.json';
import statutsConfigJson from './statuts.json';

export interface BuildingsConfig {
  capacity: {
    baseCapacityPerLevel: number;
    capacityIncreasePerLevel: number;
    capacityPerAugmentation: number;
  };
  experience: {
    xpRequiredPerLevel: number;
    xpPerIntervention: number;
  };
  augmentation: {
    timePerPlaceSeconds: number;
    timePerPlaceMinutes: number;
  };
  types: {
    [key: string]: {
      icon: string;
      color: string;
      label: string;
    };
  };
  restitution: {
    refundPercentage: number;
  };
  defaults: {
    initialLevel: number;
    initialCapacity: number;
    active: boolean;
  };
}

export interface PersonnelConfig {
  recruitment: {
    minBonuses: number;
    maxBonuses: number;
    bonusesCumulable: boolean;
  };
  retirement: {
    defaultRetirementAge: number;
    minAge: number;
    maxAge: number;
  };
  bonuses: {
    premium: {
      cumulableOutsideLimit: boolean;
    };
    activeOnlyWhenPosted: boolean;
  };
  transfer: {
    recalculateBonusesImmediately: boolean;
  };
  defaults: {
    active: boolean;
    age: number;
  };
}

export interface VehiclesConfig {
  mileage: {
    increasePerIntervention: boolean;
    impactOnBreakdown: boolean;
    noVisualWear: boolean;
  };
  breakdown: {
    probabilityBasedOnMileage: boolean;
    minMileageForBreakdown: number;
  };
  transfer: {
    bonusesApplyToCurrentBuilding: boolean;
  };
  bonuses: {
    premium: {
      additionalBonusesOutsideLimit: boolean;
    };
  };
  status: {
    available: string;
    onMission: string;
    maintenance: string;
    broken: string;
  };
  defaults: {
    status: string;
    mileage: number;
  };
}

export interface GameConfig {
  modes: {
    [key: string]: {
      name: string;
      simulationAssisted: boolean;
      errorTolerance: boolean;
      reducedDepartureTimes: boolean;
      decisionHelp: boolean;
    };
  };
  realTime: {
    callTakingStartsRealTime: boolean;
    beforeActivation: {
      staticDashboard: boolean;
      canViewStats: boolean;
      canConfigureBuildings: boolean;
      canConfigurePersonnel: boolean;
      canConfigureVehicles: boolean;
      canChooseGameMode: boolean;
    };
  };
  interventions: {
    xpPerCompletedIntervention: number;
    levelUpPerInterventions: number;
  };
  defaults: {
    gameMode: string;
    realTimeActive: boolean;
  };
}

export interface UIConfig {
  loading: {
    minLoadingTime: {
      navigation: number;
      postLogin: number;
    };
  };
  server: {
    statusCheckInterval: number;
    timeout: number;
  };
  map: {
    defaultIcon: {
      iconUrl: string;
      shadowUrl: string;
      iconSize: number[];
      iconAnchor: number[];
      popupAnchor: number[];
      shadowSize: number[];
    };
    contextMenu: {
      minDistanceFromEdge: number;
      itemHeight: number;
      padding: number;
    };
  };
  theme: {
    default: string;
    available: string[];
  };
  language: {
    default: string;
    available: string[];
  };
  settings: {
    interface: {
      defaultShowTooltips: boolean;
      defaultCompactMode: boolean;
    };
    notifications: {
      defaultEmailNotifications: boolean;
      defaultNewsletter: boolean;
      defaultInGameNotifications: boolean;
    };
  };
}

export interface APIConfig {
  endpoints: {
    buildings: {
      base: string;
      personnel: string;
      vehicules: string;
    };
    users: {
      base: string;
      ipHistory: string;
    };
    auth: {
      base: string;
      login: string;
      register: string;
    };
    saves: {
      base: string;
    };
    server: {
      base: string;
      status: string;
    };
  };
  timeouts: {
    default: number;
    short: number;
    long: number;
  };
  retry: {
    maxAttempts: number;
    delay: number;
  };
  headers: {
    contentType: string;
    userAgent: string;
  };
}

/** Clés service du fichier `statuts.json` → `statuts_planning` */
export const PLANNING_SERVICE_KEYS = ['Pompiers', 'SAMU', 'FDO'] as const;
export type PlanningServiceKey = (typeof PLANNING_SERVICE_KEYS)[number];

export interface StatutPlanningRowBase {
  id: number;
  /** Libellé court affiché en légende, ex. D5, CDGP */
  code?: string;
  nom: string;
  prio: number | null;
  couleurs: {
    backgroundColor: string;
    textColor: string;
  };
}

/** Ligne planning avec service (après aplatissement pour l’app) */
export interface StatutPlanningRow extends StatutPlanningRowBase {
  service: PlanningServiceKey;
}

/** JSON brut : planning classé par service */
export type StatutsPlanningGrouped = Record<PlanningServiceKey, StatutPlanningRowBase[]>;

export interface StatutsConfig {
  statuts: Array<{
    id: number;
    nom_statut: string;
    couleurs: {
      backgroundColor: string;
      textColor: string;
    };
  }>;
  /** Toutes les lignes (tous services), pour `.find` / légende globale */
  statuts_planning: StatutPlanningRow[];
  /** Même donnée que dans le JSON, par service */
  statuts_planning_by_service: StatutsPlanningGrouped;
}

function flattenStatutsPlanning(grouped: Partial<StatutsPlanningGrouped>): StatutPlanningRow[] {
  const out: StatutPlanningRow[] = [];
  for (const svc of PLANNING_SERVICE_KEYS) {
    const rows = grouped[svc];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      out.push({ ...row, service: svc });
    }
  }
  return out;
}

const { statuts_planning: statutsPlanningGroupedRaw, ...statutsConfigRest } = statutsConfigJson;
const statuts_planning_by_service = statutsPlanningGroupedRaw as StatutsPlanningGrouped;
const statutsConfigResolved: StatutsConfig = {
  ...statutsConfigRest,
  statuts_planning: flattenStatutsPlanning(statuts_planning_by_service),
  statuts_planning_by_service,
};

// Export des configurations
export const buildings = buildingsConfig as BuildingsConfig;
export const personnel = personnelConfig as PersonnelConfig;
export const vehicles = vehiclesConfig as VehiclesConfig;
export const game = gameConfig as GameConfig;
export const ui = uiConfig as UIConfig;
export const api = apiConfig as APIConfig;
export const statuts = statutsConfigResolved;

// Export par défaut avec toutes les configurations
const config = {
  buildings,
  personnel,
  vehicles,
  game,
  ui,
  api,
  statuts,
};

export default config;

